import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, ButtonGroup, Button, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';
import {
  EndGameChart,
  CurrentMonthChart,
  EmailEffectivenessChart,
  ListSizeChart,
  AffiliateResultsChart,
  UpsellsChart,
  FunnelMetricsChart,
  SocialTrafficChart
} from '../../components/charts';

function CCDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('month');
  const [showProfit, setShowProfit] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // AI Insights
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsSummary, setInsightsSummary] = useState('');

  // Approval Area
  const [pendingCount, setPendingCount] = useState(0);

  // Generate Now
  const [generating, setGenerating] = useState(false);
  const [generateMsg, setGenerateMsg] = useState(null);

  // Onboarding
  const [onboardingCounts, setOnboardingCounts] = useState(null);
  const [onboardingTotal, setOnboardingTotal] = useState(0);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/dashboard/analytics?period=${period}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Fetch AI insights (separate from main data to not block initial render)
  const fetchInsights = useCallback(async (forceRefresh = false) => {
    try {
      setInsightsLoading(true);
      const url = forceRefresh
        ? '/api/dashboard/insights?force_refresh=true'
        : '/api/dashboard/insights';

      const response = await fetch(url, { credentials: 'include' });

      if (response.ok) {
        const result = await response.json();
        setInsights(result.insights || []);
        setInsightsSummary(result.commentary || '');
      }
    } catch (err) {
      console.error('Insights fetch error:', err);
    } finally {
      setInsightsLoading(false);
    }
  }, []);

  // Fetch insights after data loads
  useEffect(() => {
    if (data && !insights) {
      fetchInsights();
    }
  }, [data, insights, fetchInsights]);

  // Fetch pending approval count + onboarding status
  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const response = await fetch('/api/weekly-assets/count?status=pending_approval', { credentials: 'include' });
        if (response.ok) {
          const result = await response.json();
          setPendingCount(result.count || 0);
        }
      } catch (err) {
        // Silently fail — approval count is non-critical
      }
    };
    const fetchOnboarding = async () => {
      try {
        const response = await fetch('/api/onboarding/status', { credentials: 'include' });
        if (response.ok) {
          const result = await response.json();
          const counts = result.priority_counts || { critical: 0, important: 0, optional: 0 };
          setOnboardingCounts(counts);
          setOnboardingTotal(counts.critical + counts.important + counts.optional);
        }
      } catch (err) {
        // Silently fail
      }
    };
    fetchPendingCount();
    fetchOnboarding();
  }, []);

  const handleGenerateNow = async () => {
    setGenerating(true);
    setGenerateMsg(null);
    try {
      const response = await fetch('/api/weekly-assets/generate-now', {
        method: 'POST',
        credentials: 'include',
      });
      const result = await response.json();
      if (response.ok) {
        setPendingCount(result.pending_count || 0);
        setGenerateMsg({ type: 'success', text: result.message });
      } else {
        setGenerateMsg({ type: 'danger', text: result.error || 'Generation failed' });
      }
    } catch (err) {
      setGenerateMsg({ type: 'danger', text: err.message });
    } finally {
      setGenerating(false);
      setTimeout(() => setGenerateMsg(null), 6000);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await fetch('/api/dashboard/sync', {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        // Refresh data after a short delay to allow sync to process
        setTimeout(() => {
          fetchDashboardData();
          setSyncing(false);
        }, 2000);
      }
    } catch (err) {
      console.error('Sync error:', err);
      setSyncing(false);
    }
  };

  const handleConnectAnalytics = () => {
    // TODO: Open PostHog integration modal
    alert('PostHog integration coming soon!');
  };

  const formatLastSync = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  if (loading && !data) {
    return (
      <Container className="py-4" style={styles.container}>
        <div style={styles.loadingWrapper}>
          <Spinner animation="border" style={{ color: colors.teal }} />
          <p style={styles.loadingText}>Loading dashboard...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 px-4" style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Command Center Dashboard</h1>
        <div style={styles.controls}>
          <ButtonGroup size="sm" className="me-3">
            {['day', 'week', 'month'].map((p) => (
              <Button
                key={p}
                variant={period === p ? 'warning' : 'outline-warning'}
                onClick={() => setPeriod(p)}
                style={period === p ? styles.activeButton : styles.button}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </Button>
            ))}
          </ButtonGroup>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleSync}
            disabled={syncing}
            style={styles.syncButton}
          >
            {syncing ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Syncing...
              </>
            ) : (
              'Sync Now'
            )}
          </Button>
        </div>
      </div>

      {/* Last sync info */}
      <div style={styles.syncInfo}>
        Last synced: {formatLastSync(data?.last_sync)}
      </div>

      {error && (
        <Alert variant="warning" style={styles.alert}>
          {error}
        </Alert>
      )}

      {/* AI Insights + Approval Area + Onboarding Row */}
      <Row className="g-3 mb-3">
        <Col lg={onboardingCounts && onboardingTotal > 0 ? 6 : 8}>
          <Card style={{ ...styles.card, height: '100%' }}>
            <Card.Body>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <Card.Title style={styles.cardTitle}>
                  AI Insights
                </Card.Title>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => fetchInsights(true)}
                  disabled={insightsLoading}
                  style={{ fontSize: 11, padding: '2px 8px' }}
                >
                  {insightsLoading ? 'Analyzing...' : 'Refresh'}
                </Button>
              </div>

              {insightsLoading && !insights ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <Spinner animation="border" size="sm" style={{ color: colors.teal }} />
                  <span style={{ color: colors.greyMid, marginLeft: 10, fontSize: 13 }}>
                    Analyzing your metrics...
                  </span>
                </div>
              ) : insights && insights.length > 0 ? (
                <>
                  {insightsSummary && (
                    <p style={{ color: colors.grey, fontSize: 13, marginBottom: 15, fontStyle: 'italic' }}>
                      {insightsSummary}
                    </p>
                  )}
                  <Row className="g-2">
                    {insights.map((insight, idx) => (
                      <Col md={6} key={idx}>
                        <div style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: 6,
                          padding: 12,
                          borderLeft: `3px solid ${
                            insight.type === 'positive' ? '#28a745' :
                            insight.type === 'warning' ? '#ffc107' : colors.greyMid
                          }`
                        }}>
                          <div style={{ color: colors.greyDarkest, fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                            {insight.title}
                          </div>
                          <div style={{ color: colors.grey, fontSize: 12, marginBottom: 6 }}>
                            {insight.description}
                          </div>
                          {insight.action && (
                            <div style={{ color: colors.tealDark, fontSize: 11 }}>
                              <strong>Action:</strong> {insight.action}
                            </div>
                          )}
                        </div>
                      </Col>
                    ))}
                  </Row>
                </>
              ) : (
                <p style={{ color: colors.greyMid, fontSize: 13, margin: 0 }}>
                  Not enough data yet to generate insights. Keep tracking your metrics!
                </p>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Approval Area */}
        <Col lg={onboardingCounts && onboardingTotal > 0 ? 3 : 4}>
          <Card
            style={{
              ...styles.card,
              height: '100%',
              cursor: pendingCount > 0 ? 'pointer' : 'default',
              borderColor: pendingCount > 0 ? colors.goldMid : '#e0e0e0',
            }}
            onClick={() => pendingCount > 0 && navigate('/command-center/campaign-hq/approval-area')}
            onMouseEnter={(e) => {
              if (pendingCount > 0) {
                e.currentTarget.style.borderColor = colors.goldLight;
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = pendingCount > 0 ? colors.goldMid : '#e0e0e0';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <Card.Body style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', marginBottom: 8 }}>✅</div>
              <Card.Title style={{ ...styles.cardTitle, fontSize: 14, marginBottom: 6 }}>
                Approval Area
              </Card.Title>
              <div style={{
                fontSize: 32,
                fontWeight: 700,
                color: pendingCount > 0 ? colors.goldDark : colors.greyMid,
                lineHeight: 1,
                marginBottom: 6,
              }}>
                {pendingCount}
              </div>
              <p style={{ color: colors.greyMid, fontSize: 12, margin: 0 }}>
                {pendingCount === 0
                  ? 'No assets pending'
                  : `asset${pendingCount !== 1 ? 's' : ''} pending review`}
              </p>
              {pendingCount > 0 && (
                <div style={{ marginTop: 8, color: colors.goldDark, fontSize: 11, fontWeight: 600 }}>
                  Click to review →
                </div>
              )}
              <Button
                size="sm"
                variant="outline-dark"
                style={{ marginTop: 10, fontSize: 11, padding: '3px 12px' }}
                onClick={(e) => { e.stopPropagation(); handleGenerateNow(); }}
                disabled={generating}
              >
                {generating ? <><Spinner size="sm" animation="border" style={{ width: 12, height: 12, marginRight: 4 }} /> Generating...</> : 'Generate Now'}
              </Button>
              {generateMsg && (
                <div style={{ marginTop: 6, fontSize: 11, color: generateMsg.type === 'success' ? colors.goldDark : '#dc3545' }}>
                  {generateMsg.text}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Onboarding Status — hidden when complete */}
        {onboardingCounts && onboardingTotal > 0 && (
          <Col lg={3}>
            <Card
              style={{
                ...styles.card,
                height: '100%',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/command-center/onboarding')}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = colors.goldMid;
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e0e0e0';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <Card.Body style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '1rem' }}>
                <Card.Title style={{ ...styles.cardTitle, fontSize: 14, marginBottom: 10 }}>
                  Onboarding Actions
                </Card.Title>
                <div style={{ fontSize: 28, fontWeight: 700, color: colors.greyDarkest, lineHeight: 1, marginBottom: 10 }}>
                  {onboardingTotal} <span style={{ fontSize: 13, fontWeight: 400, color: colors.greyMid }}>remaining</span>
                </div>
                <div style={{ width: '100%', textAlign: 'left' }}>
                  {onboardingCounts.critical > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, padding: '3px 8px', borderRadius: 4, backgroundColor: '#fff5f5' }}>
                      <span style={{ fontSize: 12, color: '#dc3545', fontWeight: 600 }}>Critical</span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#dc3545' }}>{onboardingCounts.critical}</span>
                    </div>
                  )}
                  {onboardingCounts.important > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, padding: '3px 8px', borderRadius: 4, backgroundColor: '#fff8e1' }}>
                      <span style={{ fontSize: 12, color: '#f57c00', fontWeight: 600 }}>Important</span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#f57c00' }}>{onboardingCounts.important}</span>
                    </div>
                  )}
                  {onboardingCounts.optional > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '3px 8px', borderRadius: 4, backgroundColor: '#f0f4f8' }}>
                      <span style={{ fontSize: 12, color: colors.greyMid, fontWeight: 600 }}>Nice to Have</span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: colors.greyMid }}>{onboardingCounts.optional}</span>
                    </div>
                  )}
                </div>
                <div style={{ marginTop: 8, color: colors.goldDark, fontSize: 11, fontWeight: 600 }}>
                  Go to Onboarding →
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      {/* 8-Panel Grid */}
      <Row className="g-3">
        {/* Row 1 */}
        <Col md={6} lg={3}>
          <Card style={styles.card}>
            <Card.Header style={{ ...styles.darkHeader, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>End Game</strong>
              <ButtonGroup size="sm">
                <Button
                  variant={!showProfit ? 'warning' : 'outline-warning'}
                  size="sm"
                  onClick={() => setShowProfit(false)}
                  style={!showProfit ? styles.toggleActive : styles.toggle}
                >
                  Sales
                </Button>
                <Button
                  variant={showProfit ? 'warning' : 'outline-warning'}
                  size="sm"
                  onClick={() => setShowProfit(true)}
                  style={showProfit ? styles.toggleActive : styles.toggle}
                >
                  Profit
                </Button>
              </ButtonGroup>
            </Card.Header>
            <Card.Body>
              <EndGameChart data={data?.end_game} showProfit={showProfit} />
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3}>
          <Card style={styles.card}>
            <Card.Header style={styles.darkHeader}>
              <strong>Total Current Month</strong>
            </Card.Header>
            <Card.Body>
              <CurrentMonthChart data={data?.current_month} />
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3}>
          <Card style={styles.card}>
            <Card.Header style={styles.darkHeader}>
              <strong>Email Effectiveness</strong>
            </Card.Header>
            <Card.Body>
              <EmailEffectivenessChart data={data?.email_effectiveness} />
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3}>
          <Card style={styles.card}>
            <Card.Header style={styles.darkHeader}>
              <strong>List Size</strong>
            </Card.Header>
            <Card.Body>
              <ListSizeChart data={data?.list_size} />
            </Card.Body>
          </Card>
        </Col>

        {/* Row 2 */}
        <Col md={6} lg={3}>
          <Card style={styles.card}>
            <Card.Header style={styles.darkHeader}>
              <strong>Affiliate Results</strong>
            </Card.Header>
            <Card.Body>
              <AffiliateResultsChart data={data?.affiliate_results} />
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3}>
          <Card style={styles.card}>
            <Card.Header style={styles.darkHeader}>
              <strong>Upsells</strong>
            </Card.Header>
            <Card.Body>
              <UpsellsChart data={data?.upsells} />
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3}>
          <Card style={styles.card}>
            <Card.Header style={styles.darkHeader}>
              <strong>Funnel Metrics</strong>
            </Card.Header>
            <Card.Body>
              <FunnelMetricsChart
                data={data?.funnel_metrics}
                onConnect={handleConnectAnalytics}
              />
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3}>
          <Card style={styles.card}>
            <Card.Header style={styles.darkHeader}>
              <strong>Social Traffic</strong>
            </Card.Header>
            <Card.Body>
              <SocialTrafficChart
                data={data?.social_traffic}
                onConnect={handleConnectAnalytics}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

const styles = {
  container: {
    backgroundColor: '#f8f9fa',
    minHeight: '100vh'
  },
  darkHeader: {
    backgroundColor: '#0a0a0a',
    color: '#ddb75e',
    fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif",
    letterSpacing: '0.1em',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
    flexWrap: 'wrap',
    gap: 10
  },
  title: {
    color: colors.greyDarkest,
    fontSize: 24,
    fontWeight: 600,
    margin: 0
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: 10
  },
  button: {
    borderColor: colors.teal,
    color: colors.tealDark,
    backgroundColor: 'transparent'
  },
  activeButton: {
    backgroundColor: colors.teal,
    borderColor: colors.teal,
    color: '#fff'
  },
  syncButton: {
    borderColor: colors.greyMid,
    color: colors.grey
  },
  syncInfo: {
    color: colors.greyMid,
    fontSize: 12,
    marginBottom: 15
  },
  alert: {
    backgroundColor: '#fff',
    borderColor: colors.teal,
    color: colors.greyDarkest
  },
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    height: '100%'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10
  },
  cardTitle: {
    color: colors.greyDarkest,
    fontSize: 14,
    fontWeight: 600,
    margin: 0
  },
  toggle: {
    fontSize: 10,
    padding: '2px 8px',
    borderColor: colors.goldLight,
    color: colors.goldLight,
    backgroundColor: 'transparent'
  },
  toggleActive: {
    fontSize: 10,
    padding: '2px 8px',
    backgroundColor: colors.goldLight,
    borderColor: colors.goldLight,
    color: colors.greyDarkest
  },
  loadingWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '60vh'
  },
  loadingText: {
    color: colors.greyMid,
    marginTop: 15
  }
};

export default CCDashboard;
